// Cookie-consent state read by PostHog and attribution capture.
//
// The server (app/services/cookie_consent.rb) resolves the visitor's region,
// Global Privacy Control signal and saved choice, and renders the result as
// window.hiikerConsent before any analytics code runs. This module reads that,
// and can write the choice back to the `hiiker_consent` cookie in the format
// the server parses: {"v":1,"a":true,"m":false,"t":"<ISO time>"}.
//
// The banner itself is wired by consent/banner_inline.js, a plain script the
// server inlines (cookie-notice filter lists block consent-named assets), which
// mirrors this module; consent/__tests__/bannerInline.test.js keeps them in step.

export type ConsentCategory = 'analytics' | 'marketing';

export interface ConsentChoice {
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentState extends ConsentChoice {
  region: 'opt_in' | 'opt_out';
  decided: boolean;
  gpc: boolean;
}

export const CONSENT_COOKIE = 'hiiker_consent';
// Keep in step with CookieConsent::VERSION and CookieConsent::MAX_AGE.
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_DAYS = 180;

// Pages rendered without the consent partial (layout-less PDF / rich-card
// renders) get nothing non-essential — the fail-safe answer.
const DENIED: ConsentState = {
  region: 'opt_in',
  decided: false,
  gpc: false,
  analytics: false,
  marketing: false,
};

// First-party cookies the trackers in each category leave behind. Third-party
// cookies (facebook.com, etc.) can't be touched from here; those stop being
// read once the tags stop loading.
const TRACKER_COOKIES: Record<ConsentCategory, RegExp[]> = {
  analytics: [/^_ga($|_)/, /^_gid$/, /^_gat/, /^_hj/, /^ph_/, /^hiiker_(first|last)_touch$/],
  marketing: [/^_gcl_/, /^_fbp$/, /^_fbc$/, /^_rdt_/, /^_twclid$/, /^__oppref$/, /^__obref$/, /^rewardful\./],
};

// PostHog persists to localStorage (persistence: 'localStorage').
const TRACKER_STORAGE_KEYS: Record<ConsentCategory, RegExp[]> = {
  analytics: [/^ph_/],
  marketing: [],
};

export function currentConsent(win: Window = window): ConsentState {
  const state = win.hiikerConsent;
  if (!state) return { ...DENIED };
  return {
    region: state.region === 'opt_out' ? 'opt_out' : 'opt_in',
    decided: Boolean(state.decided),
    gpc: Boolean(state.gpc),
    analytics: state.analytics === true,
    marketing: state.marketing === true,
  };
}

export function hasConsent(category: ConsentCategory, state: ConsentState = currentConsent()): boolean {
  return state[category] === true;
}

// GPC is an opt-out of sale/sharing, so it wins over a marketing opt-in.
export function normaliseChoice(choice: ConsentChoice, gpc: boolean): ConsentChoice {
  return { analytics: choice.analytics, marketing: gpc ? false : choice.marketing };
}

export function serializeConsent(choice: ConsentChoice, now: Date): string {
  return JSON.stringify({ v: CONSENT_VERSION, a: choice.analytics, m: choice.marketing, t: now.toISOString() });
}

export function consentCookieString(choice: ConsentChoice, now: Date, secure: boolean): string {
  const maxAge = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;
  const parts = [
    `${CONSENT_COOKIE}=${encodeURIComponent(serializeConsent(choice, now))}`,
    'path=/',
    `max-age=${maxAge}`,
    'SameSite=Lax',
  ];
  if (secure) parts.push('Secure');
  return parts.join('; ');
}

// Categories switched from on to off — their leftovers get cleared.
export function withdrawnCategories(before: ConsentChoice, after: ConsentChoice): ConsentCategory[] {
  return (['analytics', 'marketing'] as ConsentCategory[]).filter((c) => before[c] && !after[c]);
}

// Tags are rendered server-side, so any change to what's allowed needs a fresh
// page: newly allowed tags aren't on this one, and withdrawn ones can't be
// unloaded from it.
export function needsReload(before: ConsentChoice, after: ConsentChoice): boolean {
  return before.analytics !== after.analytics || before.marketing !== after.marketing;
}

export function trackerCookiesToClear(cookieNames: string[], categories: ConsentCategory[]): string[] {
  const patterns = categories.flatMap((c) => TRACKER_COOKIES[c]);
  return cookieNames.filter((name) => patterns.some((p) => p.test(name)));
}

export function trackerStorageKeysToClear(keys: string[], categories: ConsentCategory[]): string[] {
  const patterns = categories.flatMap((c) => TRACKER_STORAGE_KEYS[c]);
  return keys.filter((key) => patterns.some((p) => p.test(key)));
}

// Tracker cookies are set either host-only or on the parent domain
// (e.g. `.hiiker.app`), and expiring one needs the matching domain attribute.
export function cookieDomainVariants(hostname: string): Array<string | null> {
  const bare = hostname.replace(/^www\./, '');
  return Array.from(new Set([null, hostname, `.${bare}`]));
}

function clearTrackerStorage(categories: ConsentCategory[]): void {
  if (categories.length === 0) return;

  const names = document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter(Boolean);
  const domains = cookieDomainVariants(window.location.hostname);
  trackerCookiesToClear(names, categories).forEach((name) => {
    domains.forEach((domain) => {
      document.cookie = `${name}=; path=/; max-age=0${domain ? `; domain=${domain}` : ''}`;
    });
  });

  try {
    const keys = Object.keys(window.localStorage);
    trackerStorageKeysToClear(keys, categories).forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage blocked — nothing of ours can be in it either.
  }
}

export type ConsentAction = 'accept' | 'reject' | 'save';

// What each banner / dialog button means. "save" takes the dialog's toggles.
export function choiceForAction(action: ConsentAction, toggles: ConsentChoice): ConsentChoice {
  if (action === 'accept') return { analytics: true, marketing: true };
  if (action === 'reject') return { analytics: false, marketing: false };
  return { analytics: toggles.analytics, marketing: toggles.marketing };
}

// Persist a choice. Returns true when the page is reloading to apply it.
export function saveConsent(choice: ConsentChoice): boolean {
  const state = currentConsent();
  const next = normaliseChoice(choice, state.gpc);

  document.cookie = consentCookieString(next, new Date(), window.location.protocol === 'https:');
  clearTrackerStorage(withdrawnCategories(state, next));

  window.hiikerConsent = { ...state, ...next, decided: true };

  if (needsReload(state, next)) {
    window.location.reload();
    return true;
  }
  return false;
}
